import React from 'react';
import { generatePath } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import ControlledPhoneInput from '@/components/common/form/ControlledPhoneInput';
import ControlledTextField from '@/components/common/form/ControlledTextField';
import ControlledTextSelectField from '@/components/common/form/ControlledTextSelectField';
import RouterLink from '@/components/common/RouterLink';
import AuthRoutes from '@/constants/routing/AuthRoutes';
import EmailLabels from '@/constants/ui/EmailLabels';
import PhoneLabels from '@/constants/ui/PhoneLabels';
import WorkPhoneLabels from '@/constants/ui/WorkPhoneLabels';
import useOwnerOrgId from '@/lib/hooks/useOwnerOrgId';
import translate from '@/lib/intl/translate';
import { ConstituentType } from '@/lib/types/ConstituentPropType';
import { searchForConstituentsByEmail } from '@/services/constituentServices';
import { isValidEmail } from '@/utils/emails';

function AddRelationCreateConstituentForm({
  clearErrors,
  constituentType,
  control,
  errors,
  setError,
  watch,
}) {
  const [searchResults, setSearchResults] = React.useState(null);
  const [isSearching, setIsSearching] = React.useState(null);
  const ownerOrgId = useOwnerOrgId();
  const emailValue = watch('emailAddress');

  const phoneLabels =
    constituentType === ConstituentType.ORG ? WorkPhoneLabels : PhoneLabels;

  const checkIfEmailAddressExists = (emailAddress) => {
    return searchForConstituentsByEmail({
      ownerOrgId,
      params: { emailAddress, queryScope: 'email_address' },
    });
  };

  React.useEffect(async () => {
    if (!emailValue || !isValidEmail(emailValue)) {
      setSearchResults(null);
      return;
    }

    try {
      setIsSearching(true);
      const response = await checkIfEmailAddressExists(emailValue);
      if (response?.data?.constituents?.length) {
        setSearchResults(response?.data?.constituents?.[0]);
        setError('emailAddress', { type: 'custom' });
        return;
      }
      clearErrors('emailAddress');
      setSearchResults(null);
    } finally {
      setIsSearching(false);
    }
  }, [emailValue]);

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
      {constituentType === ConstituentType.ORG ? (
        <ControlledTextField
          className="col-span-full"
          control={control}
          error={!!errors.orgName}
          helperText={errors?.orgName?.message}
          id="add-relation-org-name"
          label={translate('COMPANY_NAME_LABEL')}
          name="orgName"
          required
          rules={{
            required: translate('ERRORS_MISSING_REQUIRED_FIELD', {
              field: translate('COMPANY_NAME_LABEL'),
            }),
          }}
        />
      ) : (
        <>
          <ControlledTextField
            className="col-span-full"
            control={control}
            error={!!errors.firstName}
            helperText={errors?.firstName?.message}
            id="add-relation-first-name"
            label={translate('FIRST_NAME_LABEL')}
            name="firstName"
            required
            rules={{
              required: translate('ERRORS_MISSING_REQUIRED_FIELD', {
                field: translate('FIRST_NAME_LABEL'),
              }),
            }}
          />
          <ControlledTextField
            className="col-span-full"
            control={control}
            error={!!errors.lastName}
            helperText={errors?.lastName?.message}
            id="add-relation-last-name"
            label={translate('LAST_NAME_LABEL')}
            name="lastName"
          />
        </>
      )}
      <ControlledTextField
        control={control}
        className="col-span-full"
        id="add-relation-display-name"
        label={translate('DISPLAY_NAME_LABEL')}
        name="displayName"
      />
      <div className="col-span-full flex flex-col md:col-span-2">
        <ControlledTextField
          className="md:col-span-2"
          control={control}
          error={!!errors.emailAddress}
          helperText={errors?.emailAddress?.message}
          id="add-relation-email-address"
          InputProps={{
            endAdornment: isSearching && (
              <InputAdornment position="end">
                <CircularProgress size="24px" />
              </InputAdornment>
            ),
          }}
          label={translate('EMAIL_ADDRESS_LABEL')}
          name="emailAddress"
          rules={{
            validate: (value) => {
              if (!value) {
                return null;
              }

              return (
                isValidEmail(value) ||
                translate('ERRORS_INVALID_FIELD', {
                  field: translate('EMAIL_ADDRESS_LABEL'),
                })
              );
            },
          }}
        />
        {searchResults && (
          <Alert icon={false} severity="warning" sx={{ marginTop: 1 }}>
            <Typography>
              {translate('CONSTITUENT_EXISTS_IN_SYSTEM', {
                displayName: searchResults.displayName,
                'link-to-constituent': () => (
                  <RouterLink
                    to={generatePath(AuthRoutes.CONSTITUENT, {
                      constituentId: searchResults.constituentId,
                    })}
                  >
                    {searchResults.displayName}
                  </RouterLink>
                ),
              })}
            </Typography>
          </Alert>
        )}
      </div>
      <ControlledTextSelectField
        formControlProps={{ className: 'md:col-span-1' }}
        control={control}
        defaultValue=""
        labelId="add-relation-email-type"
        id="add-relation-email-type"
        label={translate('TYPE_LABEL')}
        name="emailType"
        items={EmailLabels.map((label) => ({
          value: label,
          text: label,
        }))}
      />
      <ControlledPhoneInput
        className="md:col-span-2"
        control={control}
        name="phoneNumber"
        defaultValue=""
        label={translate('PHONE_NUMBER_LABEL')}
      />
      <ControlledTextSelectField
        classes={{ formControl: 'md:col-span-1 mb-0', textField: 'mb-0' }}
        control={control}
        defaultValue=""
        labelId="add-relation-phone-type"
        id="add-relation-phone-type"
        label={translate('TYPE_LABEL')}
        name="phoneType"
        items={phoneLabels.map((label) => ({
          value: label,
          text: label,
        }))}
      />
    </div>
  );
}

AddRelationCreateConstituentForm.propTypes = {
  clearErrors: PropTypes.func.isRequired,
  control: PropTypes.object.isRequired,
  constituentType: PropTypes.oneOf(Object.values(ConstituentType)).isRequired,
  errors: PropTypes.object,
  setError: PropTypes.func.isRequired,
  watch: PropTypes.func.isRequired,
};

AddRelationCreateConstituentForm.defaultProps = {
  errors: { emailAddress: {}, firstName: {}, lastName: {}, orgName: {} },
};

export default AddRelationCreateConstituentForm;
